import React, {useCallback, useMemo, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Button,
  RadioButton,
  Checkbox,
  Input,
  Textarea,
  SubTitleSmall,
  BodyMediumBold,
  BodySmall,
} from '@spaced-out/ui-design-system';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {validateAnswer, type RawAnswer} from './validate';
import {formatAnswers} from 'src/utils/ask-ai';
import type {
  AnswerMap,
  AnswerValue,
  QuestionnaireDraft,
  QuestionnaireWidgetPayload,
  QuestionnaireQuestion,
  QuestionnaireOption,
} from 'src/types/ask-ai';
import css from './questionnaire-widget.css';


export interface QuestionnaireWidgetProps {
  payload: QuestionnaireWidgetPayload;
  /** Called with the formatted Q/A text on final submit. */
  onSubmit: (qaText: string) => void;
  onMinimize: () => void;
  onClose: () => void;
  /** Disable interaction while the server is responding. */
  disabled?: boolean;
  /**
   * Read once at mount to restore wizard progress after a minimize→maximize
   * (which unmounts/remounts this widget). Returns null for a fresh start.
   */
  getInitialDraft?: () => QuestionnaireDraft | null;
  /** Persist wizard progress on every change so it survives a remount. */
  onDraftChange?: (draft: QuestionnaireDraft) => void;
}

const EMPTY_ANSWER: AnswerValue = {selectedIds: []};

function rawAnswerFor(
  question: QuestionnaireQuestion,
  answer: AnswerValue,
): RawAnswer {
  if (question.question_type === 'text') {
    return answer.text ?? '';
  }
  if (question.question_type === 'single_select') {
    return answer.selectedIds[0] ?? '';
  }
  if (question.question_type === 'multi_select') {
    return answer.selectedIds;
  }
  return undefined; // skip
}

// Seed answer state from each question's optional `default_value` (§6.8).
function buildInitialAnswers(questions: QuestionnaireQuestion[]): AnswerMap {
  const initial: AnswerMap = {};
  for (const q of questions) {
    if (q.default_value == null) {
      continue;
    }
    if (q.question_type === 'text') {
      initial[q.id] = {
        selectedIds: [],
        text: Array.isArray(q.default_value)
          ? q.default_value[0] ?? ''
          : q.default_value,
      };
    } else if (
      q.question_type === 'single_select' ||
      q.question_type === 'multi_select'
    ) {
      initial[q.id] = {
        selectedIds: Array.isArray(q.default_value)
          ? q.default_value
          : [q.default_value],
      };
    }
  }
  return initial;
}

export function QuestionnaireWidget({
  payload,
  onSubmit,
  onMinimize,
  onClose,
  disabled = false,
  getInitialDraft,
  onDraftChange,
}: QuestionnaireWidgetProps) {
  const {t} = useLabelTranslation();
  const {title, questions} = payload;
  // Wizard progress is one piece of state. We seed it from any persisted draft
  // (restored after a minimize→maximize remount) and update it only via events.
  const [draft, setDraft] = useState<QuestionnaireDraft>(
    () =>
      getInitialDraft?.() ?? {
        index: 0,
        answers: buildInitialAnswers(questions),
        touched: {},
      },
  );
  const {index, answers, touched} = draft;

  // Single update path: set local state AND persist to the store in the same
  // event-driven call. No Effect needed to "sync" — per react.dev
  // "You Might Not Need an Effect" (notify external stores from event handlers).
  const commit = useCallback(
    (next: QuestionnaireDraft) => {
      setDraft(next);
      onDraftChange?.(next);
    },
    [onDraftChange],
  );

  const question = questions[index];
  const answer = answers[question?.id ?? ''] ?? EMPTY_ANSWER;
  const isLast = index === questions.length - 1;

  const error = useMemo(() => {
    if (!question || !touched[question.id]) {
      return null;
    }
    return validateAnswer(question, rawAnswerFor(question, answer));
  }, [question, answer, touched]);

  const setAnswer = useCallback(
    (patch: Partial<AnswerValue>) => {
      commit({
        ...draft,
        answers: {
          ...draft.answers,
          [question.id]: {
            ...EMPTY_ANSWER,
            ...draft.answers[question.id],
            ...patch,
          },
        },
      });
    },
    [commit, draft, question],
  );

  const handleSelectSingle = useCallback(
    (optionId: string) => setAnswer({selectedIds: [optionId]}),
    [setAnswer],
  );

  const handleToggleMulti = useCallback(
    (optionId: string, checked: boolean) => {
      const current = draft.answers[question.id]?.selectedIds ?? [];
      const next = checked
        ? [...current, optionId]
        : current.filter((id) => id !== optionId);
      setAnswer({selectedIds: next});
    },
    [draft, question, setAnswer],
  );

  const goToNext = useCallback(() => {
    const err = validateAnswer(question, rawAnswerFor(question, answer));
    commit({
      ...draft,
      touched: {...draft.touched, [question.id]: true},
      index: err
        ? draft.index
        : Math.min(draft.index + 1, questions.length - 1),
    });
  }, [commit, draft, question, answer, questions]);

  const submit = useCallback(() => {
    // Submit is available on every step, so required questions the user never
    // visited must not be silently omitted: validate ALL, jump to the first
    // invalid one and surface its error.
    for (let qi = 0; qi < questions.length; qi += 1) {
      const q = questions[qi];
      if (q.question_type === 'skip') {
        continue;
      }
      const a = answers[q.id] ?? EMPTY_ANSWER;
      if (a.skipped) {
        continue; // explicit skip (only offered on optional questions)
      }
      const err = validateAnswer(q, rawAnswerFor(q, a));
      if (err) {
        commit({
          ...draft,
          index: qi,
          touched: {...draft.touched, [q.id]: true},
        });
        return;
      }
    }
    onSubmit(formatAnswers(questions, answers));
  }, [commit, draft, answers, questions, onSubmit]);

  const goBack = useCallback(
    () => commit({...draft, index: Math.max(0, draft.index - 1)}),
    [commit, draft],
  );

  const handleSkip = useCallback(() => {
    const nextAnswers: AnswerMap = {
      ...draft.answers,
      [question.id]: {selectedIds: [], skipped: true},
    };
    if (isLast) {
      commit({...draft, answers: nextAnswers});
      onSubmit(formatAnswers(questions, nextAnswers));
      return;
    }
    commit({
      ...draft,
      answers: nextAnswers,
      index: Math.min(draft.index + 1, questions.length - 1),
    });
  }, [commit, draft, question, isLast, onSubmit, questions]);

  if (!question) {
    return null;
  }

  const freeformSelected = (question.options ?? []).find(
    (o) => o.is_freeform && answer.selectedIds.includes(o.id),
  );

  return (
    <div className={css.card}>
      <header className={css.header}>
        <SubTitleSmall>{title}</SubTitleSmall>
        <div className={css.headerControls}>
          <Button
            type="ghost"
            iconLeftName="minus"
            size="small"
            onClick={onMinimize}
            aria-label={t('Minimize', 'Minimize')}
          />
          <Button
            type="ghost"
            iconLeftName="close"
            size="small"
            onClick={onClose}
            aria-label={t('Close', 'Close')}
          />
        </div>
      </header>

      <div className={css.body}>
        <div className={css.questionTitle}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {question.title}
          </ReactMarkdown>
        </div>

        {question.question_type === 'text' && (
          <Textarea
            classNames={{textarea: css.textArea}}
            placeholder={question.placeholder}
            value={answer.text ?? ''}
            disabled={disabled}
            onChange={(e) => setAnswer({text: e.currentTarget.value})}
          />
        )}

        {question.question_type === 'single_select' && (
          <div className={css.options}>
            {(question.options ?? []).map((opt: QuestionnaireOption) => (
              <RadioButton
                key={opt.id}
                name={question.id}
                value={opt.id}
                selectedValue={answer.selectedIds[0] ?? ''}
                disabled={disabled}
                onChange={handleSelectSingle}
                classNames={{wrapper: css.option}}
              >
                <span className={css.optionText}>
                  <BodyMediumBold color="primary">{opt.label}</BodyMediumBold>
                  {opt.description ? (
                    <BodySmall color="primary">{opt.description}</BodySmall>
                  ) : null}
                </span>
              </RadioButton>
            ))}
          </div>
        )}

        {question.question_type === 'multi_select' && (
          <div className={css.options}>
            {(question.options ?? []).map((opt: QuestionnaireOption) => (
              <Checkbox
                key={opt.id}
                value={opt.id}
                checked={answer.selectedIds.includes(opt.id)}
                disabled={disabled}
                onChange={({checked}) => handleToggleMulti(opt.id, checked)}
                classNames={{wrapper: css.option}}
              >
                <span className={css.optionText}>
                  <BodyMediumBold color="primary">{opt.label}</BodyMediumBold>
                  {opt.description ? (
                    <BodySmall color="primary">{opt.description}</BodySmall>
                  ) : null}
                </span>
              </Checkbox>
            ))}
          </div>
        )}

        {freeformSelected && (
          <div className={css.freeform}>
            <Input
              size="small"
              placeholder={freeformSelected.freeform_placeholder}
              value={answer.freeformText ?? ''}
              disabled={disabled}
              onChange={(e) => setAnswer({freeformText: e.currentTarget.value})}
            />
          </div>
        )}

        {error && <BodySmall color="danger">{error}</BodySmall>}
      </div>

      <footer className={css.footer}>
        <div className={css.footerNav}>
          <Button
            type="ghost"
            size="small"
            iconLeftName="chevron-left"
            onClick={goBack}
            disabled={index === 0 || disabled}
            ariaLabel={t('Back', 'Back')}
          />
          <Button
            type="ghost"
            size="small"
            iconLeftName="chevron-right"
            onClick={goToNext}
            disabled={isLast || disabled}
            ariaLabel={t('Next', 'Next')}
          />
        </div>
        {question.question_type === 'skip' || !question.is_required ? (
          <Button
            type="ghost"
            size="small"
            onClick={handleSkip}
            disabled={disabled}
          >
            {t('Skip', 'Skip')}
          </Button>
        ) : null}
        <Button
          type="secondary"
          size="small"
          onClick={isLast ? submit : goToNext}
          disabled={disabled}
        >
          {isLast ? t('Submit', 'Submit') : t('Next', 'Next')}
        </Button>
      </footer>
    </div>
  );
}
